import propTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import {
  ColorPicker as ColorPickerPackage,
  useColor,
} from 'react-color-palette';
import 'react-color-palette/css';
import { useFormContext } from 'react-hook-form';
import { useTheme } from 'styled-components';
import Styled from './color-picker-style';

/**
 * ETTX ColorPicker component
 *
 * @param {object} props - The props object.
 * @param {boolean} props.disabled - The disabled state of the input
 * @param {string} props.label - The label of the input
 * @param {object} props.labelStyle - The style of the label
 * @param {string} props.name - The name of the input for react-hook-form
 * @param {string} props.startColor - The start color of the color picker
 * @returns {JSX.Element} The ColorPicker component we use in ETTX Form Wrapper
 */
const ColorPicker = ({ disabled, label, labelStyle, name, startColor }) => {
  const theme = useTheme();
  const {
    COLOR_GREY5,
    COLOR_WHITE,
    COLOR_BG_DARK,
    COLOR_GREY_TEXT,
    COLOR_PRIMARY,
  } = theme;
  const [color, setColor] = useColor(startColor ?? COLOR_PRIMARY);
  const [showColorPalette, setShowColorPalette] = useState(false);
  const popupContainerRef = useRef(null);

  const { register, setValue } = useFormContext();

  const openColorPalette = () => {
    setShowColorPalette(true);
  };

  let textColor;

  if (disabled) {
    textColor = COLOR_GREY5;
  } else {
    textColor = COLOR_GREY_TEXT;
  }

  const setNewColor = (newColor) => {
    setValue(name, newColor.hex);
    setColor(newColor);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupContainerRef.current &&
        !popupContainerRef.current.contains(event.target)
      ) {
        setShowColorPalette(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClickOutside(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div>
      {label ? (
        <Styled.Label $textColor={textColor} style={labelStyle}>
          {label}
        </Styled.Label>
      ) : (
        <Styled.NoLabel />
      )}

      <Styled.FieldContainer
        onClick={openColorPalette}
        {...register(name)}
        $backgroundColor={disabled ? COLOR_BG_DARK : COLOR_WHITE}
        $borderColor={textColor}
      >
        <Styled.RoundColor $color={color.hex} />
        {showColorPalette ? (
          <Styled.RenderOnTop ref={popupContainerRef}>
            <ColorPickerPackage
              onChange={setNewColor}
              color={color}
              hideInput={['rgb', 'hsv']}
              dark
            />
          </Styled.RenderOnTop>
        ) : (
          <Styled.ColorText>{color.hex}</Styled.ColorText>
        )}
      </Styled.FieldContainer>
    </div>
  );
};

ColorPicker.propTypes = {
  /** The disabled state of the color picker */
  disabled: propTypes.bool,
  /** The label of the color picker  */
  label: propTypes.string,
  /** The style of the label */
  labelStyle: propTypes.objectOf(propTypes.string),
  /** The name of the color picker for react-hook-forms */
  name: propTypes.string.isRequired,
  /** The start color of the color picker */
  startColor: propTypes.string,
};

ColorPicker.defaultProps = {
  label: '',
  labelStyle: {},
  disabled: false,
  startColor: null,
};

export default ColorPicker;
